import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MessageCircle, Upload, Music4, Pencil, Pin, PinOff, Trash2, Ban, Flag, X, Camera, LayoutDashboard, Wallet, Shield, Bell, SlidersHorizontal, LogOut, Users, PlaySquare, TrendingUp } from "lucide-react";
import { api, uploadImage, getToken } from "../api";
import { useAuth, useGuard } from "../auth";
import { useImageViewer } from "../lightbox";
import { subscribe, unsubscribe, onEvent } from "../socket";
import { recordSubscription } from "../subscriptions";
import { mediaUrl, fmtNum, localApi } from "../config";
import { VideoCard, Avatar, Spinner, Empty } from "../components/cards";

const EditProfile = ({ creator, onClose, onSaved }) => {
  const [displayName, setDisplayName] = useState(creator.displayName || "");
  const [bio, setBio] = useState(creator.bio || "");
  const [busy, setBusy] = useState(false);
  const inputCls = "w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white outline-none focus:border-crimson/60";

  const save = async () => {
    setBusy(true);
    try {
      const u = await api.patch("/auth/profile", { displayName, bio });
      onSaved(u);
      onClose();
    } catch {} finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 px-4" onClick={onClose} data-testid="edit-profile-modal">
      <div className="w-full max-w-md rounded-3xl border border-white/10 bg-surface p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-heading text-lg font-bold text-white">Edit profile</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-white"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Display name" className={inputCls} data-testid="edit-displayname-input" />
          <textarea value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Bio" rows={4} className={inputCls} data-testid="edit-bio-input" />
          <button onClick={save} disabled={busy} className="w-full rounded-full bg-crimson py-3 font-heading text-sm font-bold text-white hover:bg-crimson-hover disabled:opacity-50" data-testid="edit-profile-save">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

const OwnVideoCard = ({ video, pinned, onPin, onDelete }) => (
  <div className="group relative">
    <VideoCard video={video} />
    <div className="absolute right-2 top-2 z-10 flex gap-1.5 opacity-0 transition-opacity group-hover:opacity-100">
      <button
        onClick={(e) => { e.preventDefault(); onPin(video); }}
        title={pinned ? "Unpin from profile" : "Pin to profile"}
        data-testid={`pin-video-${video.id}`}
        className={`rounded-full p-2 backdrop-blur-md transition-colors ${pinned ? "bg-crimson text-white" : "bg-black/60 text-slate-300 hover:text-white"}`}
      >
        {pinned ? <PinOff size={13} /> : <Pin size={13} />}
      </button>
      <button
        onClick={(e) => { e.preventDefault(); onDelete(video); }}
        title="Delete video"
        data-testid={`delete-video-${video.id}`}
        className="rounded-full bg-black/60 p-2 text-slate-300 backdrop-blur-md transition-colors hover:bg-crimson hover:text-white"
      >
        <Trash2 size={13} />
      </button>
    </div>
    {pinned && (
      <span className="absolute left-2 top-2 z-10 flex items-center gap-1 rounded-full bg-crimson px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-white">
        <Pin size={9} /> Pinned
      </span>
    )}
  </div>
);

const Shortcut = ({ icon: Icon, label, to, onClick, danger, testId }) => {
  const cls = `flex w-full items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.02] px-5 py-4 text-left transition-colors hover:border-crimson/40 hover:bg-white/[0.04]`;
  const inner = (
    <>
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${danger ? "bg-crimson/15 text-crimson" : "bg-white/[0.06] text-slate-300"}`}>
        <Icon size={18} />
      </span>
      <span className={`font-heading text-sm font-semibold ${danger ? "text-crimson" : "text-white"}`}>{label}</span>
    </>
  );
  return to ? (
    <Link to={to} className={cls} data-testid={testId}>{inner}</Link>
  ) : (
    <button onClick={onClick} className={cls} data-testid={testId}>{inner}</button>
  );
};

const ProfileView = ({ creator, videos, tracksCount, isMe, onRefresh, onCreatorUpdate }) => {
  const navigate = useNavigate();
  const { user, setUser, logout } = useAuth();
  const guard = useGuard();
  const openImage = useImageViewer();
  const [subscribed, setSubscribed] = useState(!!creator.isSubscribed);
  const [subs, setSubs] = useState(creator.subscribers ?? 0);
  const [pinnedIds, setPinnedIds] = useState(creator.pinnedVideoIds || []);
  const [editing, setEditing] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [reported, setReported] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);
  const avatarInput = useRef(null);
  const bannerInput = useRef(null);
  const cid = creator.id;

  useEffect(() => {
    setSubscribed(!!creator.isSubscribed);
    setSubs(creator.subscribers ?? 0);
    setPinnedIds(creator.pinnedVideoIds || []);
  }, [creator]);

  useEffect(() => {
    if (!cid) return;
    subscribe(`creator:${cid}`);
    const off = onEvent("subscribers", (ev) => {
      if (ev.subscribers != null) setSubs(ev.subscribers);
    });
    return () => {
      unsubscribe(`creator:${cid}`);
      off();
    };
  }, [cid]);

  const toggleSubscribe = async () => {
    if (!guard()) return;
    try {
      const r = await api.post(`/creators/${cid}/subscribe`);
      const now = r.subscribed ?? !subscribed;
      setSubscribed(now);
      if (r.subscribers != null) setSubs(r.subscribers);
      recordSubscription(cid, now);
    } catch {}
  };

  const message = async () => {
    if (!guard()) return;
    try {
      const conv = await api.post("/conversations/dm", { userId: cid });
      navigate(`/app/messages/${conv.id || conv.conversationId}`);
    } catch {}
  };

  const block = async () => {
    if (!guard()) return;
    try {
      const r = await api.post(`/creators/${cid}/block`);
      setBlocked(r.blocked ?? !blocked);
    } catch {}
  };

  const report = async () => {
    if (!guard() || reported) return;
    try {
      await api.post("/reports", { kind: "creator", targetId: cid, ref: cid, reason: "Reported from the web app" });
    } catch {}
    setReported(true);
  };

  const pinVideo = async (v) => {
    const isPinned = pinnedIds.includes(v.id);
    try {
      await api.post(`/videos/${v.id}/${isPinned ? "unpin" : "pin"}`);
      setPinnedIds((ids) => (isPinned ? ids.filter((x) => x !== v.id) : [...ids, v.id]));
    } catch {}
  };

  const deleteVideo = async (v) => {
    if (!window.confirm(`Delete "${v.title}"? This cannot be undone.`)) return;
    try {
      await api.del(`/videos/${v.id}`);
      onRefresh?.();
    } catch {}
  };

  const changeImage = async (file, purpose) => {
    if (!file) return;
    setImgBusy(true);
    try {
      const key = await uploadImage(file, purpose);
      let u = await api.patch("/auth/profile", purpose === "avatar" ? { avatarKey: key } : { bannerKey: key });
      if (!u?.id) u = await api.get("/auth/me");
      if (isMe) setUser((prev) => ({ ...prev, ...u }));
      onCreatorUpdate?.(u);
    } catch {} finally {
      setImgBusy(false);
    }
  };

  const removeImage = async (purpose) => {
    setImgBusy(true);
    try {
      await api.del(`/auth/${purpose}`);
      let u = await api.get("/auth/me").catch(() => null);
      const patch = purpose === "avatar" ? { avatar: null } : { banner: null };
      if (isMe) setUser((prev) => ({ ...prev, ...(u || {}), ...patch }));
      onCreatorUpdate?.({ ...(u || {}), ...patch });
    } catch {} finally {
      setImgBusy(false);
    }
  };

  const sorted = [...videos].sort((a, b) => (pinnedIds.includes(b.id) ? 1 : 0) - (pinnedIds.includes(a.id) ? 1 : 0));

  return (
    <div className="mx-auto max-w-6xl" data-testid="profile-page">
      <div className="relative h-40 overflow-hidden rounded-3xl bg-surface sm:h-56" data-testid="profile-cover">
        {mediaUrl(creator.banner) && (
          <img
            src={mediaUrl(creator.banner)}
            alt=""
            className="h-full w-full cursor-zoom-in object-cover"
            onClick={() => openImage(mediaUrl(creator.banner))}
            data-testid="profile-cover-image"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian to-transparent" />
        {isMe && (
          <>
            <button
              onClick={() => bannerInput.current?.click()}
              disabled={imgBusy}
              className="absolute right-3 top-3 flex items-center gap-2 rounded-full bg-black/60 px-4 py-2 text-xs font-semibold text-white backdrop-blur-md transition-colors hover:bg-black/80"
              data-testid="change-cover-button"
            >
              <Camera size={14} /> Change cover
            </button>
            {creator.banner && (
              <button
                onClick={() => removeImage("banner")}
                disabled={imgBusy}
                className="absolute right-3 top-14 flex items-center gap-2 rounded-full bg-black/60 px-4 py-2 text-xs font-semibold text-white backdrop-blur-md transition-colors hover:bg-crimson"
                data-testid="remove-cover-button"
              >
                <Trash2 size={14} /> Remove cover
              </button>
            )}
            <input ref={bannerInput} type="file" accept="image/*" className="hidden" data-testid="banner-input" onChange={(e) => changeImage(e.target.files?.[0], "banner")} />
          </>
        )}
      </div>
      <div className="-mt-10 flex flex-wrap items-end gap-5 px-4 sm:px-8">
        <div className="relative rounded-full border-4 border-obsidian">
          <Avatar user={creator} size={92} />
          {isMe && (
            <>
              <button
                onClick={() => avatarInput.current?.click()}
                disabled={imgBusy}
                title="Change profile picture"
                className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full bg-crimson text-white shadow-lg transition-colors hover:bg-crimson-hover"
                data-testid="change-avatar-button"
              >
                <Camera size={14} />
              </button>
              {creator.avatar && (
                <button
                  onClick={() => removeImage("avatar")}
                  disabled={imgBusy}
                  title="Remove profile picture"
                  className="absolute -bottom-1 -left-1 flex h-8 w-8 items-center justify-center rounded-full border border-white/20 bg-surface text-slate-300 shadow-lg transition-colors hover:text-crimson"
                  data-testid="remove-avatar-button"
                >
                  <Trash2 size={13} />
                </button>
              )}
              <input ref={avatarInput} type="file" accept="image/*" className="hidden" data-testid="avatar-input" onChange={(e) => changeImage(e.target.files?.[0], "avatar")} />
            </>
          )}
        </div>
        <div className="min-w-0 flex-1 pb-1">
          <h1 className="truncate font-heading text-2xl font-black tracking-tight text-white" data-testid="profile-name">
            {creator.displayName || creator.username}
          </h1>
          <p className="text-sm text-slate-500">@{creator.username}</p>
        </div>
        <div className="flex flex-wrap gap-3 pb-2">
          {isMe ? (
            <>
              <button onClick={() => setEditing(true)} className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 font-heading text-xs font-bold text-slate-200 hover:border-crimson/50" data-testid="profile-edit-button">
                <Pencil size={14} /> Edit profile
              </button>
              <Link to="/app/upload?kind=video" className="flex items-center gap-2 rounded-full bg-crimson px-5 py-2.5 font-heading text-xs font-bold text-white hover:bg-crimson-hover" data-testid="profile-upload-video-button">
                <Upload size={14} /> Upload video
              </Link>
              <Link to="/app/upload?kind=music" className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 font-heading text-xs font-bold text-slate-200 hover:border-crimson/50" data-testid="profile-upload-music-button">
                <Music4 size={14} /> Upload music
              </Link>
            </>
          ) : (
            <>
              <button onClick={toggleSubscribe} data-testid="profile-subscribe-button" className={`rounded-full px-5 py-2.5 font-heading text-xs font-bold transition-colors ${subscribed ? "border border-white/15 text-slate-300" : "bg-crimson text-white hover:bg-crimson-hover"}`}>
                {subscribed ? "Subscribed" : "Subscribe"}
              </button>
              <button onClick={message} className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 font-heading text-xs font-bold text-slate-200 hover:border-crimson/50" data-testid="profile-message-button">
                <MessageCircle size={14} /> Message
              </button>
              <button onClick={block} className={`flex items-center gap-2 rounded-full border px-5 py-2.5 font-heading text-xs font-bold transition-colors ${blocked ? "border-crimson/50 text-crimson" : "border-white/15 text-slate-200 hover:border-crimson/50"}`} data-testid="profile-block-button">
                <Ban size={14} /> {blocked ? "Blocked" : "Block"}
              </button>
              <button onClick={report} className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 font-heading text-xs font-bold text-slate-200 hover:border-crimson/50" data-testid="profile-report-button">
                <Flag size={14} /> {reported ? "Reported" : "Report"}
              </button>
            </>
          )}
        </div>
      </div>
      {creator.bio && <p className="mt-4 whitespace-pre-wrap px-4 text-sm leading-relaxed text-slate-400 sm:px-8" data-testid="profile-bio">{creator.bio}</p>}

      <div className="mt-6 flex gap-8 px-4 sm:px-8" data-testid="profile-stats-row">
        <div>
          <p className="font-heading text-xl font-black text-white" data-testid="stat-subscribers">{fmtNum(subs)}</p>
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Subscribers</p>
        </div>
        <div>
          <p className="font-heading text-xl font-black text-white" data-testid="stat-videos">{fmtNum(videos.length)}</p>
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Videos</p>
        </div>
        <div>
          <p className="font-heading text-xl font-black text-white" data-testid="stat-tracks">{fmtNum(tracksCount ?? 0)}</p>
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Tracks</p>
        </div>
      </div>

      {isMe && (
        <div className="mt-10 px-4 sm:px-8">
          <div className="grid gap-3 sm:grid-cols-2" data-testid="profile-shortcuts">
            <Shortcut icon={LayoutDashboard} label="Dashboard" to="/app/dashboard" testId="shortcut-dashboard" />
            <Shortcut icon={Wallet} label="Wallet" to="/app/wallet" testId="shortcut-wallet" />
            <Shortcut icon={Upload} label="Upload" to="/app/upload" testId="shortcut-upload" />
            <Shortcut icon={Ban} label="Blocked" to="/app/blocked" testId="shortcut-blocked" />
            <Shortcut icon={Shield} label="Security" to="/app/security" testId="shortcut-security" />
            <Shortcut icon={Bell} label="Notifications" to="/app/notifications" testId="shortcut-notifications" />
            <Shortcut icon={SlidersHorizontal} label="Privacy options" onClick={() => window.googlefc?.showRevocationMessage?.()} testId="shortcut-privacy-options" />
            <Shortcut icon={LogOut} label="Log out" danger onClick={() => { logout(); navigate("/app"); }} testId="shortcut-logout" />
          </div>
        </div>
      )}

      {isMe && <EstimatedEarnings />}

      <h2 className="mb-4 mt-10 px-4 font-heading text-lg font-bold text-white sm:px-8">Videos</h2>
      <div className="px-4 sm:px-8">
        {sorted.length === 0 ? (
          <Empty>{isMe ? "You haven't uploaded anything yet — use the upload buttons above." : "No videos yet."}</Empty>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
            {sorted.map((v) =>
              isMe ? (
                <OwnVideoCard key={v.id} video={v} pinned={pinnedIds.includes(v.id)} onPin={pinVideo} onDelete={deleteVideo} />
              ) : (
                <VideoCard key={v.id} video={v} />
              )
            )}
          </div>
        )}
      </div>

      {editing && (
        <EditProfile
          creator={creator}
          onClose={() => setEditing(false)}
          onSaved={(u) => {
            onCreatorUpdate?.(u);
            if (isMe && u) setUser((prev) => ({ ...prev, ...u }));
          }}
        />
      )}
    </div>
  );
};

// Estimated earnings per video (revenue per view) — visible to the creator only.
const EstimatedEarnings = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    localApi("GET", "/earnings/videos", undefined, getToken())
      .then((r) => setRows(Array.isArray(r) ? r : []))
      .catch(() => setRows([]));
  }, []);
  if (rows === null) return null;
  if (rows.length === 0) return null;
  const total = rows.reduce((s, r) => s + (r.estimated || 0), 0);
  return (
    <div className="mt-10 px-4 sm:px-8" data-testid="estimated-earnings">
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-white">
            <TrendingUp size={18} className="text-emerald-400" /> Estimated earnings
          </h2>
          <span className="font-heading text-lg font-black text-emerald-400" data-testid="estimated-earnings-total">£{total.toFixed(2)}</span>
        </div>
        <p className="mb-4 text-xs leading-relaxed text-slate-500">
          These are estimates from your views (revenue per view). They're added to your Wallet when the admin runs revenue.
        </p>
        <div className="space-y-1">
          {rows.map((r) => (
            <div key={r.video_id} className="flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 hover:bg-white/[0.03]" data-testid={`estimate-${r.video_id}`}>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-white">{r.title}</p>
                <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{r.type} · {fmtNum(r.views)} views</p>
              </div>
              <div className="text-right">
                <p className="font-heading text-sm font-bold text-emerald-400">£{(r.estimated || 0).toFixed(2)}</p>
                {r.paid_total > 0 && <p className="font-mono text-[9px] uppercase tracking-wider text-slate-600">paid £{r.paid_total.toFixed(2)}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const CreatorProfile = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tracksCount, setTracksCount] = useState(0);

  const load = () => {
    api.get(`/creators/${id}`).then(async (r) => {
      const creator = r.creator ?? r;
      let videos = r.videos ?? [];
      if (videos.length === 0) {
        videos = await api.get(`/videos?creatorId=${id}&limit=48`).then((vr) => (Array.isArray(vr) ? vr : vr.videos || [])).catch(() => []);
        videos = videos.filter((v) => v.creatorId === id);
      }
      setData({ creator, videos });
    }).catch(() => setData({ creator: null, videos: [] }));
    api.get("/music").then((r) => setTracksCount((Array.isArray(r) ? r : r.tracks || []).filter((t) => t.creatorId === id).length)).catch(() => {});
  };

  useEffect(() => {
    setData(null);
    load();
  }, [id]);

  if (data === null) return <Spinner />;
  if (!data.creator) return <Empty>Creator not found.</Empty>;
  return (
    <ProfileView
      creator={data.creator}
      videos={data.videos}
      tracksCount={tracksCount}
      isMe={user?.id === data.creator.id}
      onRefresh={load}
      onCreatorUpdate={(u) => setData((d) => ({ ...d, creator: { ...d.creator, ...u } }))}
    />
  );
};

export const MyProfile = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [tracksCount, setTracksCount] = useState(0);

  const load = () => {
    if (!user?.id) return;
    api.get(`/videos?creatorId=${user.id}&limit=48`)
      .then((r) => (Array.isArray(r) ? r : r.videos || []).filter((v) => v.creatorId === user.id))
      .then(setVideos)
      .catch(() => {});
    api.get("/music")
      .then((r) => setTracksCount((Array.isArray(r) ? r : r.tracks || []).filter((t) => t.creatorId === user.id).length))
      .catch(() => {});
  };

  useEffect(() => {
    load();
  }, [user?.id]);

  if (!user) {
    return (
      <div className="mx-auto max-w-md py-20 text-center" data-testid="profile-signin-notice">
        <Empty>
          <p>Sign in to see your profile, upload videos and manage your content.</p>
          <button onClick={() => navigate("/app/login")} className="mt-4 rounded-full bg-crimson px-6 py-2.5 font-heading text-xs font-bold text-white hover:bg-crimson-hover" data-testid="profile-signin-button">
            Sign in
          </button>
        </Empty>
      </div>
    );
  }
  return <ProfileView creator={user} videos={videos} tracksCount={tracksCount} isMe onRefresh={load} />;
};
